/* ==========================================================================
   FNDRY — Video carousel

   One video plays at a time, always in the centre slot. When it ends the
   carousel steps forward. Everything the CSS needs is two numbers per slide:
   --o (slot offset) and data-off (the same number, clamped, for styling).

   The rest state is rendered by Liquid, so with no JS the section still shows
   a correct five-up with the first clip playing — it just never advances.
   ========================================================================== */
(function () {
  'use strict';

  var SEL = '[data-fndry-video-carousel]';
  var MAX_SLOT = 3; /* beyond this a slide is parked off-stage at opacity 0 */

  function now() {
    return window.performance && window.performance.now ? window.performance.now() : Date.now();
  }

  function onMq(mq, fn) {
    if (mq.addEventListener) mq.addEventListener('change', fn);
    else if (mq.addListener) mq.addListener(fn);
  }

  function offMq(mq, fn) {
    if (mq.removeEventListener) mq.removeEventListener('change', fn);
    else if (mq.removeListener) mq.removeListener(fn);
  }

  /* A duration token may be authored in ms or s. */
  function cssDuration(el, prop, fallback) {
    var raw = String(getComputedStyle(el).getPropertyValue(prop) || '').trim();
    var n = parseFloat(raw);
    if (isNaN(n)) return fallback;
    if (raw.indexOf('ms') > -1) return n;
    if (raw.indexOf('s') > -1) return n * 1000;
    return n;
  }

  function Carousel(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-fvc-slide]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-fvc-dot]'));
    this.stage = root.querySelector('[data-fvc-stage]');
    this.track = root.querySelector('[data-fvc-track]') || root.querySelector('.fndry-vc__track');
    this.count = this.slides.length;
    this.active = 0;
    this.userPaused = false;
    this.suspended = false;
    this.swallowClick = false;
    this.raf = 0;

    this.fallbackMs = (parseFloat(root.getAttribute('data-fallback')) || 8) * 1000;
    this.labels = {
      play: root.getAttribute('data-label-play') || 'Play video',
      pause: root.getAttribute('data-label-pause') || 'Pause video',
      show: root.getAttribute('data-label-show') || 'Show video'
    };

    /* Negate the queries the stylesheet uses; never invent their complement. */
    this.mqDesk = window.matchMedia('(min-width: 750px)');
    this.mqWide = window.matchMedia('(min-width: 1500px)');
    this.mqStill = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.dur = cssDuration(root, '--fvc-dur', 700);
    this.timerBase = 0;
    this.timerStart = 0;

    this.bind();
  }

  /* ---- geometry --------------------------------------------------------- */

  /* Shortest signed offset of slide i from the active one. */
  Carousel.prototype.offsetOf = function (i) {
    var n = this.count;
    var o = ((i - this.active) % n + n) % n;
    if (o > n / 2) o -= n;
    return o;
  };

  /* Signed number of forward steps between two indexes, shortest way round. */
  Carousel.prototype.stepBetween = function (from, to) {
    var n = this.count;
    var d = to - from;
    if (d > n / 2) d -= n;
    if (d < -n / 2) d += n;
    return d;
  };

  Carousel.prototype.span = function () {
    return this.mqWide.matches ? 2 : this.mqDesk.matches ? 1 : 0;
  };

  Carousel.prototype.place = function (slide, off, animate) {
    var slot = Math.max(-MAX_SLOT, Math.min(MAX_SLOT, off));
    if (!animate) slide.style.transition = 'none';
    slide.style.setProperty('--o', String(slot));
    slide.setAttribute('data-off', String(slot));
    if (!animate) {
      void slide.offsetWidth; /* flush, so the next write animates */
      slide.style.transition = '';
    }
  };

  /*
   * step = how far the active index just moved. A slide whose new offset is
   * not simply (previous - step) has wrapped: it leaves one edge and arrives
   * at the other. Animate it out, teleport it while invisible, animate it in.
   */
  Carousel.prototype.layout = function (step, animate) {
    var self = this;

    this.slides.forEach(function (slide) {
      var off = self.offsetOf(slide._index);
      var prev = slide._off;
      var continuous = typeof prev === 'number' ? prev - step : off;

      if (slide._wrapTimer) {
        clearTimeout(slide._wrapTimer);
        slide._wrapTimer = 0;
      }

      if (!animate || typeof prev !== 'number' || continuous === off) {
        self.place(slide, off, animate);
      } else {
        self.place(slide, continuous, true);
        slide._wrapTimer = setTimeout(function () {
          slide._wrapTimer = 0;
          self.place(slide, off + (off < 0 ? -1 : 1), false);
          self.place(slide, off, true);
        }, Math.max(120, self.dur * 0.5));
      }

      slide._off = off;
    });

    this.syncReach();
  };

  /* Off-stage slides are out of the tab order and out of the accessibility
     tree. On desktop the neighbours are visible content, so they stay in. */
  Carousel.prototype.syncReach = function () {
    var span = this.span();

    this.slides.forEach(function (slide) {
      var visible = Math.abs(slide._off || 0) <= span;
      if (visible) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');

      Array.prototype.forEach.call(slide.querySelectorAll('a[href], button'), function (el) {
        if (visible) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });
  };

  /*
   * The stylesheet gives the track its height. If a theme rule, a stale asset
   * or an unsupported property takes that away, the cards are still laid out
   * and still the right width — they just have nowhere to be, and the section
   * renders as a headline above a row of dots. Measure once, and if the track
   * has collapsed, write the height the stylesheet meant to produce.
   */
  Carousel.prototype.ensureHeight = function () {
    if (!this.track || !this.slides.length) return;

    this.track.style.removeProperty('height');

    var w = this.slides[0].getBoundingClientRect().width;
    if (!w) return;

    var ratio = parseFloat(getComputedStyle(this.track).getPropertyValue('--fvc-ratio')) || 175;
    /* P15 sitewide 2026-10-23: the cap is the stylesheet's 66vh / 76vh as
       measured at load (--fndry-vh, header.liquid), not today's innerHeight,
       which follows the phone toolbar. */
    var unit = parseFloat(document.documentElement.style.getPropertyValue('--fndry-vh'));
    var vh100 = unit > 0 ? unit * 100 : window.innerHeight;
    var cap = vh100 * (this.mqDesk.matches ? 0.76 : 0.66);
    var want = Math.min((w * ratio) / 100, cap);

    /* A little slack: vh and innerHeight need not agree to the pixel. */
    if (this.track.getBoundingClientRect().height >= want * 0.92) return;

    this.track.style.height = Math.round(want) + 'px';
  };

  /* ---- playback --------------------------------------------------------- */

  Carousel.prototype.goTo = function (index, animate) {
    if (this.count < 1) return;
    var target = ((index % this.count) + this.count) % this.count;
    var step = this.stepBetween(this.active, target);
    this.active = target;
    this.layout(step, animate !== false && !this.mqStill.matches);
    this.activate();
  };

  Carousel.prototype.next = function () {
    this.goTo(this.active + 1, true);
  };

  Carousel.prototype.activate = function () {
    var self = this;

    this.timerBase = 0;
    this.timerStart = 0;
    this.userPaused = false;
    this.lastProgress = 0;

    this.slides.forEach(function (slide, i) {
      var video = slide._video;
      var hit = slide._hit;

      if (i === self.active) {
        slide.setAttribute('data-active', 'true');
        slide.removeAttribute('data-paused');
        if (hit) hit.setAttribute('aria-label', self.labels.pause);
      } else {
        slide.removeAttribute('data-active');
        slide.removeAttribute('data-paused');
        if (hit) hit.setAttribute('aria-label', self.labels.show + ' ' + (i + 1));
        if (video) {
          video.pause();
          try {
            video.currentTime = 0;
          } catch (e) {
            /* metadata not in yet — harmless */
          }
        }
      }
    });

    this.dots.forEach(function (dot, i) {
      if (i === self.active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
      dot.style.setProperty('--fvc-p', '0');
      dot._fvcP = null;
    });

    var current = this.slides[this.active];
    if (current && current._video) {
      try {
        current._video.currentTime = 0;
      } catch (e) {
        /* as above */
      }
    }

    if (!this.suspended) this.play();
  };

  Carousel.prototype.play = function () {
    var slide = this.slides[this.active];
    if (!slide) return;
    this.warm();

    slide.removeAttribute('data-paused');
    this.timerStart = now();
    this.loop();

    if (slide._video) {
      var p = slide._video.play();
      if (p && p.catch) {
        p.catch(function () {
          /* Autoplay refused. The fallback timer takes over from here, so the
             section rotates through posters rather than stalling on one. */
          slide._blocked = true;
        });
      }
    }
  };

  Carousel.prototype.pause = function (byUser) {
    var slide = this.slides[this.active];
    if (!slide) return;

    if (this.timerStart) {
      this.timerBase += now() - this.timerStart;
      this.timerStart = 0;
    }
    if (slide._video) slide._video.pause();

    if (byUser) {
      this.userPaused = true;
      slide.setAttribute('data-paused', 'true');
    }
  };

  Carousel.prototype.toggle = function () {
    if (this.userPaused) {
      this.userPaused = false;
      this.play();
      var hit = this.slides[this.active] && this.slides[this.active]._hit;
      if (hit) hit.setAttribute('aria-label', this.labels.pause);
    } else {
      this.pause(true);
      var h = this.slides[this.active] && this.slides[this.active]._hit;
      if (h) h.setAttribute('aria-label', this.labels.play);
    }
  };

  Carousel.prototype.suspend = function () {
    if (this.suspended) return;
    this.suspended = true;
    if (!this.userPaused) this.pause(false);
  };

  Carousel.prototype.resume = function () {
    if (!this.suspended) return;
    this.suspended = false;
    if (!this.userPaused && !document.hidden) this.play();
  };

  /* ---- progress --------------------------------------------------------- */

  /* P15 2026-10-23: the frame loop used to run from page load to page close,
     every frame, on- or off-screen - it kept the home page's main thread awake
     the whole visit. It now runs only while a clip is advancing: it stops when
     the carousel leaves the screen, the tab is hidden or she pauses (tick()
     already ignored those frames), and play() starts it again. */
  Carousel.prototype.loop = function () {
    var self = this;
    if (this.raf) return;
    this.raf = window.requestAnimationFrame(function () {
      self.raf = 0;
      if (self.userPaused || self.suspended) return;
      self.tick();
      self.loop();
    });
  };

  Carousel.prototype.tick = function () {
    if (this.userPaused || this.suspended) return; /* dot holds its last value */

    var slide = this.slides[this.active];
    if (!slide) return;

    var video = slide._video;
    var elapsed = this.timerBase + (this.timerStart ? now() - this.timerStart : 0);
    var hasVideo = video && !slide._videoBroken && !slide._blocked;
    var progress;
    var ended = false;

    if (hasVideo && isFinite(video.duration) && video.duration > 0 && video.currentTime > 0) {
      /* Playing. The 'ended' event moves the carousel on, not this. */
      progress = video.currentTime / video.duration;
    } else if (hasVideo) {
      /* Still buffering. Hold at zero rather than filling from the timer and
         then jumping backwards when the first frame lands. */
      progress = 0;
      if (elapsed > this.fallbackMs * 2) ended = true;
    } else {
      /* No video on this block, a broken file, or autoplay was refused. */
      progress = elapsed / this.fallbackMs;
      if (progress >= 1) ended = true;
    }

    progress = Math.max(0, Math.min(1, progress));
    this.lastProgress = progress;

    var dot = this.dots[this.active];
    var value = progress.toFixed(4);
    if (dot && dot._fvcP !== value) {
      dot._fvcP = value;
      dot.style.setProperty('--fvc-p', value);
    }

    if (ended && this.count > 1) this.next();
  };

  /* ---- wiring ----------------------------------------------------------- */

  Carousel.prototype.bind = function () {
    var self = this;

    this.slides.forEach(function (slide, i) {
      slide._index = i;
      slide._video = slide.querySelector('video');
      slide._hit = slide.querySelector('[data-fvc-hit]');

      if (slide._video) {
        var video = slide._video;
        video.muted = true;
        video.setAttribute('muted', '');
        video.loop = self.count === 1;

        video.addEventListener('playing', function () {
          slide._blocked = false;
        });
        video.addEventListener('ended', function () {
          if (slide._index === self.active && self.count > 1) self.next();
        });
        video.addEventListener('error', function () {
          slide._videoBroken = true;
        });
      }

      if (slide._hit) {
        slide._hit.addEventListener('click', function () {
          if (self.swallowClick) return;
          if (slide._index === self.active) self.toggle();
          else self.goTo(slide._index, true);
        });
      }
    });

    this.dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        self.goTo(i, true);
      });
    });

    this._onMq = function () {
      self.syncReach();
      self.ensureHeight();
    };
    onMq(this.mqDesk, this._onMq);
    onMq(this.mqWide, this._onMq);

    /* P15 sitewide 2026-10-23: a resize that only changes the height is the
       phone toolbar collapsing or coming back; the track's size no longer
       depends on it, so only a change of width (or a desktop window)
       re-checks it. */
    this._w = document.documentElement.clientWidth;
    this._fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    this._onResize = function () {
      if (self._resizeRaf) return;
      self._resizeRaf = window.requestAnimationFrame(function () {
        self._resizeRaf = 0;
        var w = document.documentElement.clientWidth;
        if (w === self._w && !self._fine.matches) return;
        self._w = w;
        self.ensureHeight();
      });
    };
    window.addEventListener('resize', this._onResize);

    this.bindDrag();

    this._onKey = function (e) {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        self.goTo(self.active - 1, true);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        self.goTo(self.active + 1, true);
      }
    };
    this.root.addEventListener('keydown', this._onKey);

    if ('IntersectionObserver' in window) {
      /* P15 sitewide 2026-10-23: nothing plays until the carousel is on
         screen - the first clip used to start (and download) at page load,
         nine sections down. The observer's first report starts it when it
         is in view, exactly as a scroll into view always has. */
      this.suspended = true;
      this.io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            self.inView = entry.isIntersecting;
            if (entry.isIntersecting) self.resume();
            else self.suspend();
          });
        },
        { threshold: 0.2 }
      );
      this.io.observe(this.root);

      /* Posters and clip headers a screen and a half ahead, so the cards
         are dressed long before they can be seen. */
      this.warmIo = new IntersectionObserver(
        function (entries) {
          if (!entries[entries.length - 1].isIntersecting) return;
          self.warm();
          self.warmIo.disconnect();
        },
        { rootMargin: '150% 0px 150% 0px' }
      );
      this.warmIo.observe(this.root);
    } else {
      this.warm();
    }

    this._onVisibility = function () {
      if (document.hidden) self.suspend();
      else if (self.inView !== false) self.resume(); /* never start a clip that is off screen */
    };
    document.addEventListener('visibilitychange', this._onVisibility);

    this.goTo(0, false);
    this.ensureHeight();
    this.loop();
  };

  /*
   * Pointer, wheel and touch all resolve to the same gesture: a horizontal
   * throw past a threshold steps one slide. Deliberately not a live 1:1 drag —
   * off-stage slides are hidden by slot, so there is nothing to follow the
   * cursor in from the edge on the narrow layouts. This keeps desktop and
   * mobile behaving identically.
   */
  Carousel.prototype.bindDrag = function () {
    if (!this.stage) return;

    var self = this;
    var stage = this.stage;
    var x0 = null;
    var y0 = null;
    var pid = null;
    var moved = false;

    function begin(x, y) {
      x0 = x;
      y0 = y;
      moved = false;
    }

    function reset() {
      x0 = null;
      y0 = null;
      moved = false;
      stage.removeAttribute('data-fvc-dragging');
    }

    /* A throw ends over a card, and that card holds a play toggle and a link.
       Hold the click that the browser is about to fire. */
    function holdClick() {
      self.swallowClick = true;
      window.setTimeout(function () {
        self.swallowClick = false;
      }, 400);
    }

    function finish(x, y) {
      if (x0 === null) return;
      var dx = x - x0;
      var dy = y - y0;
      reset();
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        holdClick();
        self.goTo(self.active + (dx < 0 ? 1 : -1), true);
      }
    }

    /* Swallow in the capture phase so the product link is covered too, not
       just the play toggle, which checks the flag itself. */
    stage.addEventListener(
      'click',
      function (e) {
        if (!self.swallowClick) return;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );

    stage.addEventListener('dragstart', function (e) {
      if (x0 !== null) e.preventDefault();
    });

    /* ---- mouse and pen ---- */
    if (window.PointerEvent) {
      stage.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return; /* handled below */
        if (e.button !== 0) return;
        pid = e.pointerId;
        begin(e.clientX, e.clientY);
      });

      stage.addEventListener('pointermove', function (e) {
        if (pid === null || e.pointerId !== pid || x0 === null || moved) return;
        if (Math.abs(e.clientX - x0) <= 6) return;
        moved = true;
        stage.setAttribute('data-fvc-dragging', 'true');
      });

      /*
       * Resolved on the window, not the stage, so a throw that runs off the
       * edge still lands. Pointer capture would do the same job but retargets
       * the compatibility click to the capturing element, which costs the play
       * toggle every click that follows a nudge.
       */
      this._onPointerUp = function (e) {
        if (pid === null || e.pointerId !== pid) return;
        pid = null;
        finish(e.clientX, e.clientY);
      };

      this._onPointerCancel = function (e) {
        /* A touch pan fires pointercancel with no pointerdown of ours behind
           it. Without this guard it would wipe the in-flight touch gesture. */
        if (pid === null || e.pointerId !== pid) return;
        pid = null;
        reset();
      };

      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerCancel);
    }

    /* ---- trackpad and horizontal wheel ---- */
    var acc = 0;
    var accAt = 0;
    var lock = 0;

    stage.addEventListener(
      'wheel',
      function (e) {
        /* Vertical intent belongs to the page. Never take it. */
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();

        var t = now();
        if (t - accAt > 200) acc = 0;
        accAt = t;
        if (t < lock) return;

        acc += e.deltaX;
        if (Math.abs(acc) < 40) return;

        lock = t + Math.max(320, self.dur);
        var dir = acc > 0 ? 1 : -1;
        acc = 0;
        self.goTo(self.active + dir, true);
      },
      { passive: false }
    );

    /* ---- touch ---- */
    stage.addEventListener(
      'touchstart',
      function (e) {
        if (!e.touches || e.touches.length !== 1) return;
        begin(e.touches[0].clientX, e.touches[0].clientY);
      },
      { passive: true }
    );

    stage.addEventListener(
      'touchend',
      function (e) {
        var t = e.changedTouches && e.changedTouches[0];
        if (t) finish(t.clientX, t.clientY);
        else reset();
      },
      { passive: true }
    );

    stage.addEventListener('touchcancel', reset, { passive: true });
  };

  Carousel.prototype.warm = function () {
    if (this.warmed) return;
    this.warmed = true;
    this.slides.forEach(function (slide) {
      var video = slide._video;
      if (!video) return;
      var poster = video.getAttribute('data-poster');
      if (poster && !video.getAttribute('poster')) video.setAttribute('poster', poster);
      video.preload = 'metadata';
    });
  };

  Carousel.prototype.destroy = function () {
    if (this.raf) window.cancelAnimationFrame(this.raf);
    if (this.warmIo) this.warmIo.disconnect();
    if (this._resizeRaf) window.cancelAnimationFrame(this._resizeRaf);
    if (this.io) this.io.disconnect();
    window.removeEventListener('resize', this._onResize);
    if (this._onPointerUp) window.removeEventListener('pointerup', this._onPointerUp);
    if (this._onPointerCancel) window.removeEventListener('pointercancel', this._onPointerCancel);
    this.root.removeEventListener('keydown', this._onKey);
    document.removeEventListener('visibilitychange', this._onVisibility);
    offMq(this.mqDesk, this._onMq);
    offMq(this.mqWide, this._onMq);
    this.slides.forEach(function (slide) {
      if (slide._wrapTimer) clearTimeout(slide._wrapTimer);
      if (slide._video) slide._video.pause();
    });
    this.root.removeAttribute('data-fvc-ready');
  };

  /* ---- init ------------------------------------------------------------- */

  function init(scope) {
    var roots = (scope || document).querySelectorAll(SEL);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.getAttribute('data-fvc-ready') === 'true') return;
      root.setAttribute('data-fvc-ready', 'true');
      try {
        root.fndryCarousel = new Carousel(root);
      } catch (e) {
        /* Liquid already rendered a correct static layout; leave it alone. */
        root.setAttribute('data-fvc-failed', 'true');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector ? e.target.querySelector(SEL) : null;
    if (root && root.fndryCarousel) root.fndryCarousel.destroy();
  });

  document.addEventListener('shopify:block:select', function (e) {
    var el = e.target;
    if (!el || !el.getAttribute || el.getAttribute('data-fvc-slide') === null) return;
    var root = el.closest ? el.closest(SEL) : null;
    if (root && root.fndryCarousel) {
      root.fndryCarousel.goTo(parseInt(el.getAttribute('data-index'), 10) || 0, true);
      root.fndryCarousel.pause(true);
    }
  });
})();